import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Formik } from "formik";
import * as Yup from "yup";
import { toast } from '../../common/Toast/toast';
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../../../context/AuthContext";
import NewCustomAsyncDropdown from "../../common/NewCustomAsyncSelectDropdown.jsx";
import { getcourses, getcoursesById } from "../../../../services/CourseModule";
import {
  createSyllabusTopics,
  updateSyllabusTopics,
  getSyllabusTopicsById,
  fetchTopicsForCourse,
} from "../../../../services/SyllabusTopicServices.js";
import "../../Trainer/TrainerDashboard.css";
import "../../common/lms-pages.css";

const COURSE_PAGE = 10;

/** Normalize Mongo/API ids and populated { id } joins for comparisons. */
const toIdString = (v) => {
  if (v == null || v === "") return "";
  if (typeof v === "object") return String(v.id ?? v._id ?? "").trim();
  return String(v).trim();
};

const topicKey = (topic) => toIdString(topic?.id ?? topic?._id);

/** The API writes a root topic as "NA", but has also returned null and "". */
const isRootSyllabusTopic = (topic) => {
  const p = topic?.parentTopicId;
  return (
    p == null || p === "" || p === "NA" || String(p).toLowerCase() === "null"
  );
};

const validationSchema = Yup.object({
  courseId: Yup.string()
    .trim()
    .required("Choose the course this syllabus belongs to."),
  /* The main topic's own heading. Required when one is being created --
     "NA" is this form's word for "a new top-level topic" -- and ignored
     when the sub-topics are being hung under a topic that already
     exists. */
  mainTopic: Yup.string().when("parentTopicId", {
    is: (v) => String(v ?? "") === "NA",
    then: (s) => s.trim().min(2, "Name the main topic.").required("Name the main topic."),
    otherwise: (s) => s.strip(),
  }),
  /* Sub-topics are OPTIONAL under a new main topic -- a heading on its
     own is a perfectly good syllabus entry, and more can be added later.
     Under an existing topic they are the only thing being added, so at
     least one has to be named. */
  topics: Yup.array().when("parentTopicId", {
    is: (v) => String(v ?? "") === "NA",
    then: (a) => a,
    otherwise: (a) =>
      a.test(
        "one-named-topic",
        "Enter at least one sub-topic name.",
        (list) => (list || []).some((t) => String(t || "").trim().length >= 2),
      ),
  }),
  /* "NA" is a main topic; anything else is the parent's id. The empty
     string is the third state: sub-topic chosen, parent not picked yet.
     It has to fail, or the topic would silently be created at the top
     level -- the exact confusion this field was redesigned to end. */
  parentTopicId: Yup.string().test(
    "parent-when-sub",
    "Choose the topic this sits under.",
    (value) => String(value ?? "").trim() !== "",
  ),
});

/**
 * Rendered on its own route, and inside the syllabus list as a dialog.
 * `recordId` stands in for the route param when embedded, and onDone /
 * onCancel replace the navigation the standalone page does. `courseId`
 * is the ?courseId= of the embedded form: the syllabus being edited is
 * already known, so the picker opens on it instead of empty.
 */
function SyllabusForm({ embedded = false, recordId, courseId, onDone, onCancel }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: routeId } = useParams();
  const { hasPermission } = useAuth();

  const urlParams = new URLSearchParams(location.search);
  const courseIdFromUrl = toIdString(courseId) || urlParams.get("courseId");

  // One topic is what an edit edits. Adding is the other mode: a course,
  // where the new topics hang, and the names themselves.
  const editTopicId =
    toIdString(recordId) ||
    toIdString(routeId) ||
    urlParams.get("editTopicId") ||
    "";
  const isTopicEdit = Boolean(editTopicId);

  const [loading, setLoading] = useState(true);
  const [initialValues, setInitialValues] = useState({
    courseId: "",
    parentTopicId: "NA",
    // The heading a new main topic is created under, and the sub-topics
    // typed beneath it in the same sitting.
    mainTopic: "",
    topics: [""],
  });
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [editingTopic, setEditingTopic] = useState(null);

  // Course picker — paged and searched on the server, so it carries its
  // own cursor rather than filtering a list held in memory.
  const [courseOptions, setCourseOptions] = useState([]);
  const [hasMoreCourses, setHasMoreCourses] = useState(true);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [courseSearch, setCourseSearch] = useState("");
  const courseFetchRef = useRef(false);
  const courseOpenedRef = useRef(false);
  // Which fetch is the current one. A search typed while a page is still
  // in flight used to be dropped on the floor by the guard below, leaving
  // the picker showing one query and paging another.
  const courseReqRef = useRef(0);
  // The cursor, kept where the scroll handler can read it without waiting
  // for a render.
  const coursePageRef = useRef(1);

  const [parentOptions, setParentOptions] = useState([]);
  const [parentsLoading, setParentsLoading] = useState(false);

  const hasShownToast = useRef(false);

  useEffect(() => {
    if (!hasPermission("list-syllabuses", "add", "lms")) {
      if (!hasShownToast.current) {
        hasShownToast.current = true;
        toast.error("You do not have permission to add syllabus.");
        navigate("/");
      }
    }
  }, [hasPermission, navigate]);

  const loadCourses = useCallback(async (page = 1, search = "") => {
    const req = courseReqRef.current + 1;
    courseReqRef.current = req;
    courseFetchRef.current = true;
    setLoadingCourses(true);
    try {
      const res = await getcourses(COURSE_PAGE, page, search, {
        includeTotal: false,
      });
      // A newer search has already taken over; this answer is stale.
      if (courseReqRef.current !== req) return;
      const list = Array.isArray(res?.data) ? res.data : [];
      const options = list.map((course) => ({
        value: toIdString(course.id) || course.id,
        label: course.name,
      }));
      // Guarded by id so a repeated page cannot double up an option.
      setCourseOptions((prev) => {
        if (page === 1) return options;
        const seen = new Set(prev.map((o) => o.value));
        return [...prev, ...options.filter((o) => !seen.has(o.value))];
      });
      // The API says whether another page exists when it is asked without
      // a total. Falling back on "a full page means more" is only a guess,
      // and it stops the list dead one page early on an exact multiple.
      const hasNext = res?.pagination?.hasNext;
      setHasMoreCourses(
        typeof hasNext === "boolean" ? hasNext : list.length === COURSE_PAGE,
      );
      coursePageRef.current = page;
    } catch (error) {
      console.error("Error fetching courses:", error);
      if (courseReqRef.current === req) toast.error("Could not load courses.");
    } finally {
      if (courseReqRef.current === req) {
        setLoadingCourses(false);
        courseFetchRef.current = false;
      }
    }
  }, []);

  const handleCourseOpen = useCallback(() => {
    if (courseOpenedRef.current) return;
    courseOpenedRef.current = true;
    loadCourses(1, "");
  }, [loadCourses]);

  const handleCourseSearch = useCallback(
    (value) => {
      courseOpenedRef.current = true;
      setCourseSearch(value);
      setHasMoreCourses(true);
      coursePageRef.current = 1;
      loadCourses(1, value);
    },
    [loadCourses],
  );

  const handleCourseScrollEnd = useCallback(() => {
    if (!hasMoreCourses || courseFetchRef.current) return;
    loadCourses(coursePageRef.current + 1, courseSearch);
  }, [hasMoreCourses, courseSearch, loadCourses]);

  const handleCourseRefresh = useCallback(async () => {
    courseOpenedRef.current = true;
    setCourseSearch("");
    setHasMoreCourses(true);
    setCourseOptions([]);
    coursePageRef.current = 1;
    await loadCourses(1, "");
  }, [loadCourses]);

  /** Only root topics can be a parent — the tree this page draws is two deep. */
  const loadParentOptions = useCallback(async (courseId, excludeId = "") => {
    if (!courseId) {
      setParentOptions([]);
      return;
    }
    setParentsLoading(true);
    try {
      const topics = await fetchTopicsForCourse(courseId);
      const roots = (topics || [])
        .filter((t) => topicKey(t) && isRootSyllabusTopic(t))
        .filter((t) => topicKey(t) !== toIdString(excludeId))
        .sort((a, b) =>
          String(a.topic || "").localeCompare(String(b.topic || "")),
        );
      setParentOptions(
        roots.map((t) => ({ value: topicKey(t), label: t.topic || "Untitled" })),
      );
    } catch (error) {
      console.error("Error fetching syllabus topics for course:", error);
      setParentOptions([]);
    } finally {
      setParentsLoading(false);
    }
  }, []);

  // First load: the record being edited, or the course handed in by the URL.
  useEffect(() => {
    let cancelled = false;

    const resolveCourse = async (courseId) => {
      const cid = toIdString(courseId);
      if (!cid) return null;
      try {
        const res = await getcoursesById(cid);
        if (res?.data) {
          return {
            value: toIdString(res.data.id) || cid,
            label: res.data.name,
          };
        }
      } catch (error) {
        console.error("Error resolving course:", error);
      }
      return { value: cid, label: "Selected course" };
    };

    (async () => {
      setLoading(true);
      try {
        if (isTopicEdit) {
          const res = await getSyllabusTopicsById(editTopicId);
          const raw = res?.data;
          const topic = Array.isArray(raw) ? raw[0] : raw;
          if (!topic) throw new Error("Topic not found");

          const course = await resolveCourse(topic.courseId);
          if (cancelled) return;

          setEditingTopic(topic);
          setSelectedCourse(course);
          setInitialValues({
            courseId: course?.value || "",
            parentTopicId: isRootSyllabusTopic(topic)
              ? "NA"
              : toIdString(topic.parentTopicId),
            topics: [topic.topic || topic.name || ""],
          });
          if (course?.value) {
            await loadParentOptions(course.value, topicKey(topic));
          }
          return;
        }

        const course = courseIdFromUrl
          ? await resolveCourse(courseIdFromUrl)
          : null;
        if (cancelled) return;
        setSelectedCourse(course);
        setInitialValues({
          courseId: course?.value || "",
          parentTopicId: "NA",
          topics: [""],
        });
        if (course?.value) await loadParentOptions(course.value);
      } catch (error) {
        console.error("Failed to load syllabus form:", error);
        if (!cancelled) {
          toast.error("Failed to load the topic. Please try again.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isTopicEdit, editTopicId, courseIdFromUrl, loadParentOptions]);

  const leave = useCallback(() => {
    if (embedded) onCancel?.();
    else navigate("/list-syllabuses");
  }, [embedded, onCancel, navigate]);

  const finish = useCallback(() => {
    if (embedded) {
      onDone?.();
      return;
    }
    setTimeout(() => navigate("/list-syllabuses"), 1200);
  }, [embedded, onDone, navigate]);

  const parentLabel = useMemo(() => {
    if (!editingTopic || isRootSyllabusTopic(editingTopic)) return "Top level";
    const id = toIdString(editingTopic.parentTopicId);
    return parentOptions.find((o) => o.value === id)?.label || "A parent topic";
  }, [editingTopic, parentOptions]);

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    const names = (values.topics || [])
      .map((t) => String(t || "").trim())
      .filter(Boolean);

    if (!names.length) {
      toast.warning("Please add at least one topic.");
      setSubmitting(false);
      return;
    }

    try {
      if (isTopicEdit) {
        await updateSyllabusTopics(editTopicId, {
          courseId: values.courseId,
          topic: names[0],
          // An edit renames a topic; where it hangs is not what it changes.
          parentTopicId: editingTopic?.parentTopicId ?? "NA",
        });
        toast.success("Topic updated successfully!");
        finish();
        return;
      }

      /* A new main topic is created FIRST, and its id is what the
         sub-topics below it hang on. That is the whole point of typing
         them together: the parent does not exist yet when the form is
         filled in, so it cannot be picked from a list. */
      let parentId = values.parentTopicId || "NA";
      let createdMain = 0;

      if (parentId === "NA") {
        const heading = String(values.mainTopic || "").trim();
        if (!heading) {
          toast.warning("Name the main topic.");
          setSubmitting(false);
          return;
        }
        const res = await createSyllabusTopics({
          courseId: values.courseId,
          topic: heading,
          parentTopicId: "NA",
        });
        const newId = toIdString(res?.data?.id ?? res?.data?._id);
        if (!newId) {
          // Without the id the sub-topics would silently be created at the
          // top level, which is the confusion this flow exists to end.
          throw new Error("The main topic was saved but did not come back with an id.");
        }
        parentId = newId;
        createdMain = 1;
      }

      for (const topic of names) {
        await createSyllabusTopics({
          courseId: values.courseId,
          topic,
          parentTopicId: parentId,
        });
      }

      const total = createdMain + names.length;
      toast.success(
        createdMain && names.length
          ? `Main topic added with ${names.length} sub-topic${names.length === 1 ? "" : "s"}.`
          : createdMain
            ? "Main topic added."
            : `${total} sub-topic${total === 1 ? "" : "s"} added.`,
      );

      if (embedded) {
        finish();
        return;
      }
      // The standalone page stays open so a run of topics can be typed in
      // one sitting; the course and the parent are what it keeps.
      /* The course and the choice are what a run of entries keeps; the
         heading and its sub-topics are cleared, or the next save would
         create the same main topic again. */
      resetForm({ values: { ...values, mainTopic: "", topics: [""] } });
      await loadParentOptions(values.courseId);
    } catch (error) {
      console.error("Failed to save syllabus topics:", error);
      toast.error(
        error?.response?.data?.message || "Failed to save. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="att-picklist-foot">
        <span className="pg-spinner att-spinner" aria-hidden="true" />
        <span>{isTopicEdit ? "Loading topic..." : "Loading courses..."}</span>
      </div>
    );
  }

  return (
    /* --picker, like the trainer form: this dialog's course list PAGES,
       and .att-embed alone takes max-height and overflow off every
       option list so it expands in place. A box that cannot scroll never
       fires onScroll, so onMenuScrollToBottom never runs and the picker
       stops at its first batch however far you drag. */
    <div className={embedded ? "att-embed att-embed--picker" : "pg"}>
      {!embedded && (
        <header className="pg-head">
          <div className="pg-head-main">
            <h4 className="pg-title">
              {isTopicEdit ? "Edit topic" : "Add syllabus"}
            </h4>
          </div>
          <div className="pg-head-actions">
            <button type="button" className="pg-btn" onClick={leave}>
              Back to syllabuses
            </button>
          </div>
        </header>
      )}

      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({
          values,
          errors,
          touched,
          isSubmitting,
          setFieldValue,
          handleSubmit: submit,
        }) => {
          const err = (field) => (touched[field] ? errors[field] : "");
          const named = (values.topics || []).filter(
            (t) => String(t || "").trim().length >= 2,
          );
          /* What the sub-topic fields hang under, by name: the heading
             typed above, or the topic chosen from the list. Shown in the
             section heading so "sub-topic" is never abstract. */
          const newMain = values.parentTopicId === "NA";
          const subHeading = newMain
            ? String(values.mainTopic || "").trim()
            : parentOptions.find((o) => o.value === values.parentTopicId)?.label || "";

          /* Ready means the form knows it can save BEFORE the click, not
             after. A new main topic needs its heading; sub-topics under an
             existing one need a parent and at least one name. */
          const parentChosen = String(values.parentTopicId ?? "").trim() !== "";
          const ready = newMain
            ? Boolean(values.courseId) && subHeading.length >= 2
            : Boolean(values.courseId) && parentChosen && named.length > 0;

          const setTopic = (index, value) => {
            const next = [...values.topics];
            next[index] = value;
            setFieldValue("topics", next);
          };

          const addTopic = () => setFieldValue("topics", [...values.topics, ""]);

          const dropTopic = (index) => {
            if (values.topics.length === 1) return;
            setFieldValue(
              "topics",
              values.topics.filter((_, i) => i !== index),
            );
          };

          return (
            <form className="att-form" noValidate onSubmit={submit}>
              {/* Editing renames one topic, so what it belongs to is stated
                  once as a fact rather than offered as a control. */}
              {isTopicEdit && (
                <dl className="att-context">
                  <div>
                    <dt>Course</dt>
                    <dd>{selectedCourse?.label || "—"}</dd>
                  </div>
                  <div>
                    <dt>Under</dt>
                    <dd>{parentLabel}</dd>
                  </div>
                </dl>
              )}

              <div className="att-stage">
                <div className="att-stage-inner">
                  <section className="att-step">
                    {!isTopicEdit && (
                      <>
                        <div className="pg-field">
                          <label className="pg-label">Course</label>
                          <NewCustomAsyncDropdown
                            options={courseOptions}
                            value={selectedCourse ? [selectedCourse] : []}
                            onChange={(picked) => {
                              const next =
                                Array.isArray(picked) && picked.length
                                  ? picked[0]
                                  : null;
                              setSelectedCourse(next);
                              setFieldValue("courseId", next?.value || "");
                              // The parent list belongs to the course, so it
                              // cannot outlive the course being changed.
                              setFieldValue("parentTopicId", "NA");
                              setParentOptions([]);
                              if (next?.value) loadParentOptions(next.value);
                            }}
                            placeholder="Search for a course..."
                            isMulti={false}
                            isSearchable
                            searchInControl
                            isLoading={loadingCourses}
                            onMenuOpen={handleCourseOpen}
                            onInputChange={handleCourseSearch}
                            onMenuScrollToBottom={handleCourseScrollEnd}
                            onRefresh={handleCourseRefresh}
                            loadingMessage="Loading courses..."
                            noOptionsMessage="No courses available"
                            noSearchResultsMessage="No courses found"
                          />
                          {err("courseId") && (
                            <p className="att-error">{err("courseId")}</p>
                          )}
                        </div>

                        {/* The KIND is now a choice you make, not a default
                            you have to notice. It was one dropdown whose
                            first option happened to be "Top level", so
                            whether you were creating a main topic or a
                            sub-topic was something you inferred from a
                            select you may never have opened. Two options,
                            each saying what it does, and the parent picker
                            appears only when it is actually needed. */}
                        <div className="pg-field">
                          <span className="pg-label">What are you adding?</span>
                          <div
                            className="syl-kind"
                            role="radiogroup"
                            aria-label="What are you adding?"
                          >
                            <label
                              className={`syl-kind-opt${
                                values.parentTopicId === "NA" ? " is-on" : ""
                              }`}
                            >
                              <input
                                type="radio"
                                name="syl-kind"
                                checked={values.parentTopicId === "NA"}
                                disabled={!values.courseId}
                                onChange={() => setFieldValue("parentTopicId", "NA")}
                              />
                              <b>A main topic</b>
                              <small>
                                Name the heading, then type its sub-topics
                                underneath. The sub-topics are optional.
                              </small>
                            </label>

                            <label
                              className={`syl-kind-opt${
                                values.parentTopicId !== "NA" ? " is-on" : ""
                              }${parentOptions.length === 0 ? " is-off" : ""}`}
                            >
                              <input
                                type="radio"
                                name="syl-kind"
                                checked={values.parentTopicId !== "NA"}
                                disabled={
                                  !values.courseId ||
                                  parentsLoading ||
                                  parentOptions.length === 0
                                }
                                /* Empty, not the first option: picking the
                                   parent is the whole point of this choice,
                                   so it is asked rather than guessed. */
                                onChange={() => setFieldValue("parentTopicId", "")}
                              />
                              <b>Sub-topics under an existing topic</b>
                              <small>
                                {parentsLoading
                                  ? "Reading this course's topics…"
                                  : parentOptions.length === 0
                                    ? "This course has no topics to sit under yet — add a main topic first."
                                    : "Pick the topic they belong to, then type them."}
                              </small>
                            </label>
                          </div>
                          {!values.courseId && (
                            <p className="att-note syl-note">
                              Pick a course first — its topics are what a sub-topic
                              can sit under.
                            </p>
                          )}
                        </div>

                        {values.parentTopicId !== "NA" && (
                          <div className="pg-field">
                            <label className="pg-label" htmlFor="syl-parent">
                              Which topic does it sit under?
                            </label>
                            <select
                              id="syl-parent"
                              className="pg-select"
                              value={values.parentTopicId}
                              disabled={parentsLoading}
                              onChange={(e) =>
                                setFieldValue("parentTopicId", e.target.value)
                              }
                            >
                              <option value="">Choose a topic…</option>
                              {parentOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                            {err("parentTopicId") && (
                              <p className="att-error">{err("parentTopicId")}</p>
                            )}
                          </div>
                        )}
                      </>
                    )}

                    {/* The main topic's own heading, typed here rather than
                        picked from a list, because this is where a syllabus
                        actually starts: a heading, and the things under it. */}
                    {!isTopicEdit && values.parentTopicId === "NA" && (
                      <div className="pg-field">
                        <label className="pg-label" htmlFor="syl-main">
                          Main topic
                        </label>
                        <input
                          id="syl-main"
                          type="text"
                          className="pg-input"
                          placeholder="e.g. Core Java"
                          value={values.mainTopic}
                          onChange={(e) => setFieldValue("mainTopic", e.target.value)}
                        />
                        {err("mainTopic") && (
                          <p className="att-error">{err("mainTopic")}</p>
                        )}
                      </div>
                    )}

                    {/* The heading that says what the fields below belong to.
                        It names the parent -- the one typed above, or the one
                        chosen from the list -- so "sub-topic" is never an
                        abstract word on a form. */}
                    {!isTopicEdit && (
                      <h5 className="syl-sub-head">
                        Sub-topics
                        {subHeading ? (
                          <>
                            {" of "}
                            <span className="syl-sub-of">{subHeading}</span>
                          </>
                        ) : null}
                        {values.parentTopicId === "NA" && (
                          <span className="syl-sub-opt">optional</span>
                        )}
                      </h5>
                    )}

                    {/* One field per sub-topic: a syllabus is usually typed as
                        a run of names, not one at a time. */}
                    <div className="syl-topics">
                      {values.topics.map((topic, index) => (
                        <div className="syl-topic" key={index}>
                          <div className="pg-field syl-topic-field">
                            <label
                              className="pg-label"
                              htmlFor={`syl-topic-${index}`}
                            >
                              {isTopicEdit
                                ? "Topic name"
                                : `Sub-topic ${index + 1}`}
                            </label>
                            <input
                              id={`syl-topic-${index}`}
                              type="text"
                              className="pg-input"
                              placeholder="e.g. Introduction to Spring Boot"
                              value={topic}
                              onChange={(e) => setTopic(index, e.target.value)}
                            />
                          </div>
                          {!isTopicEdit && values.topics.length > 1 && (
                            <button
                              type="button"
                              className="pg-btn syl-topic-drop"
                              onClick={() => dropTopic(index)}
                              aria-label={`Remove topic ${index + 1}`}
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      ))}
                    </div>

                    {!isTopicEdit && (
                      <div className="syl-topics-foot">
                        <button
                          type="button"
                          className="pg-btn"
                          onClick={addTopic}
                        >
                          Add another sub-topic
                        </button>
                        <span className="syl-topics-tally">
                          {named.length} ready to save
                        </span>
                      </div>
                    )}

                    {typeof errors.topics === "string" && touched.topics && (
                      <p className="att-error">{errors.topics}</p>
                    )}
                  </section>
                </div>
              </div>

              <div className="att-actions">
                <button type="button" className="pg-btn" onClick={leave}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="pg-btn pg-btn--main"
                  disabled={isSubmitting || !ready}
                >
                  {isSubmitting ? (
                    <>
                      <span
                        className="pg-spinner att-spinner"
                        aria-hidden="true"
                      />
                      {isTopicEdit ? "Updating..." : "Saving..."}
                    </>
                  ) : isTopicEdit ? (
                    "Update topic"
                  ) : named.length > 1 ? (
                    `Add ${named.length} topics`
                  ) : (
                    "Add topic"
                  )}
                </button>
              </div>
            </form>
          );
        }}
      </Formik>
    </div>
  );
}

export default SyllabusForm;
